import React, { JSX, useCallback } from "react";
import { useTranslation } from "react-i18next";

import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";

import {
	AddEvent,
	ChangeEvent,
	DeleteEvent,
	Event,
	EventGroup
} from "utils/history/Event";
import { asTitle } from "utils/Utils";

import { RatingInfo } from "core/field/FieldService";
import { ScoreResolution } from "evaluation/ScoreResolution";

import RatingSettingsRow from "./RatingSettingsRow";

export type RatingSettingsProps = {
	scoreInfo: RatingInfo[];
	disabled?: boolean;

	onChange: (event: Event) => void;

	// The score resolution belongs to the trajectory template. The rating-field
	// override (RatingFieldRow) reuses this editor for its own score scale but
	// owns no resolution, so both are optional and the selector is only
	// rendered when a handler is supplied.
	scoreResolution?: ScoreResolution;
	onChangeResolution?: (resolution: ScoreResolution) => void;
};

function RatingSettings(props: RatingSettingsProps): JSX.Element {
	const [t] = useTranslation();

	const DEFAULT_ROW: RatingInfo = {
		name: "",
		description: "",
		colour: "#faaf00"
	};

	const onChangeScores = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>): void => {
			const newSize = Number.parseInt(event.target.value, 10);
			const originalSize = props.scoreInfo.length;

			const events: Event[] = [];
			for (let index = originalSize - 1; index >= newSize; --index) {
				events.push(
					new DeleteEvent(`/${index}`, props.scoreInfo[index])
				);
			}

			for (let index = originalSize; index < newSize; ++index) {
				events.push(new AddEvent(`/${index}`, DEFAULT_ROW));
			}

			if (events.length > 0) {
				const eventGroup = new EventGroup(events);
				props.onChange(eventGroup);
			}
		},
		[props.onChange, props.scoreInfo]
	);

	const onChangeName = useCallback(
		(index: number, name: string): void => {
			const oldValue = props.scoreInfo[index].name;
			props.onChange(new ChangeEvent(`/${index}/name`, oldValue, name));
		},
		[props.onChange, props.scoreInfo]
	);

	const onChangeDescription = useCallback(
		(index: number, description: string): void => {
			const oldValue = props.scoreInfo[index].description;
			props.onChange(
				new ChangeEvent(`/${index}/description`, oldValue, description)
			);
		},
		[props.onChange, props.scoreInfo]
	);

	const onChangeColour = useCallback(
		(index: number, colour: string): void => {
			const oldValue = props.scoreInfo[index].colour;
			props.onChange(
				new ChangeEvent(`/${index}/colour`, oldValue, colour)
			);
		},
		[props.onChange, props.scoreInfo]
	);

	const changeResolution = props.onChangeResolution;
	const onChangeResolution = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>): void => {
			changeResolution?.(event.target.value as ScoreResolution);
		},
		[changeResolution]
	);

	return (
		<Grid>
			<Grid container spacing={3}>
				<Grid item xs={3} sm={2} md={1}>
					<TextField
						required
						type="number"
						fullWidth
						label={t("score_other")}
						value={props.scoreInfo.length}
						disabled={props.disabled}
						onChange={onChangeScores}
						variant="standard"
					/>
				</Grid>
				{changeResolution !== undefined && (
					<Grid item xs={9} sm={6} md={4}>
						<TextField
							select
							fullWidth
							label={asTitle(t("scoreResolution"))}
							helperText={t("scoreResolutionInfo")}
							value={
								props.scoreResolution ??
								ScoreResolution.LAST_SCORE
							}
							disabled={props.disabled}
							onChange={onChangeResolution}
							variant="standard">
							<MenuItem value={ScoreResolution.LAST_SCORE}>
								{t("scoreResolutionLastScore")}
							</MenuItem>
							<MenuItem value={ScoreResolution.TRAILING_ACHIEVED}>
								{t("scoreResolutionTrailingAchieved")}
							</MenuItem>
						</TextField>
					</Grid>
				)}
			</Grid>

			<Table size="small">
				<TableHead>
					<TableRow>
						<TableCell>{asTitle(t("score"))}</TableCell>
						<TableCell>{asTitle(t("scoreName"))}</TableCell>
						<TableCell>{asTitle(t("colour"))}</TableCell>
						<TableCell>{asTitle(t("info"))}</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{props.scoreInfo.map((row, index) => (
						<RatingSettingsRow
							key={index}
							index={index}
							value={row}
							disabled={props.disabled}
							onChangeName={onChangeName}
							onChangeDescription={onChangeDescription}
							onChangeColour={onChangeColour}
						/>
					))}
				</TableBody>
			</Table>
		</Grid>
	);
}

export default RatingSettings;
